import React, { useState } from 'react';
import AgentRegistration from './AgentRegistration';
import AgentManagement from './AgentManagement';
import AgentPerformance from './AgentPerformance';
import { Link } from "react-router-dom";
import { useServerRole } from "../utils/permissions";

const AgentDashboard = () => {
  const [activeTab, setActiveTab] = useState('registration');

  // SECURITY: this whole dashboard — creating agents, viewing full
  // agent records, sales stats, activate/deactivate — is gated behind
  // manage_agents now (see the redesigned routes/agents.js). This
  // check only controls what renders; the actual enforcement is the
  // auth + requireAdmin(['manage_agents']) middleware on each
  // endpoint the three tabs below call. A non-admin who somehow got
  // past this UI gate would still just get 403s from every action.
  const { isAdmin, hasPermission, loading: roleLoading } = useServerRole();
  const canManageAgents = isAdmin && hasPermission('manage_agents');
  const hasSession = !!localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY);

  // NOTE: this used to prefetch every user via /api/auth/allusers and
  // hand the whole list to AgentRegistration as a `contacts` prop for
  // local filtering — that's gone now. AgentRegistration searches the
  // backend directly (search-users) as the admin types, so there's
  // nothing left for this component to prefetch. This also removes
  // the `useParams`/`userId` dependency this dashboard had no real
  // use for otherwise.

  // State to trigger refreshes in child components
  const [refreshAgents, setRefreshAgents] = useState(false);
  const [refreshPerformance, setRefreshPerformance] = useState(false);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Trigger refresh when switching to these tabs
    if (tab === 'management') setRefreshAgents(prev => !prev);
    if (tab === 'performance') setRefreshPerformance(prev => !prev);
  };

  const handleAgentCreated = () => {
    setRefreshAgents(prev => !prev);
    setRefreshPerformance(prev => !prev);
    setActiveTab('management');
  };

  if (roleLoading) {
    return <div className="agent-dashboard"><p style={{ textAlign: 'center', padding: '2rem' }}>Loading...</p></div>;
  }

  if (!hasSession) {
    return (
      <div className="agent-dashboard">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Please sign in to access the Agent Management System.</p>
          <Link to="/login" style={{ color: '#0f1f3d', fontWeight: 700, textDecoration: 'underline' }}>
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  if (!canManageAgents) {
    return (
      <div className="agent-dashboard">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <h2>Access Denied</h2>
          <p>You don't have permission to access the Agent Management System.</p>
          <Link to="/">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-dashboard">
      <div className="dashboard-header">
        <h2><Link to='/'>Home</Link>||Agent Management System</h2>
        <div className="tab-buttons">
          <button
            className={`tab-button ${activeTab === 'registration' ? 'active' : ''}`}
            onClick={() => handleTabChange('registration')}
          >
            Register New Agent
          </button>
          <button
            className={`tab-button ${activeTab === 'management' ? 'active' : ''}`}
            onClick={() => handleTabChange('management')}
          >
            Manage Agents
          </button>
          <button
            className={`tab-button ${activeTab === 'performance' ? 'active' : ''}`}
            onClick={() => handleTabChange('performance')}
          >
            Agent Performance
          </button>
        </div>
      </div>

      <div className="tab-content">
        {activeTab === 'registration' && (
          <AgentRegistration onAgentCreated={handleAgentCreated} />
        )}
        
        {activeTab === 'management' && (
          <AgentManagement key={refreshAgents} />
        )}
        
        {activeTab === 'performance' && (
          <AgentPerformance key={refreshPerformance} />
        )}
      </div>
    </div>
  );
};

export default AgentDashboard;
